/**
 * 🧭 NAVIGATION_HELP — Source unique de vérité pour l'aide navigation CoachDIA.
 *
 * Organisation par INTENTIONS UTILISATEUR (pas par architecture interne).
 * Chaque item respecte le triptyque pédagogique :
 *   ce_que_c_est · utilite · a_utiliser_quand
 *
 * Filtrage côté composant :
 *   - `role: 'affiliate'` → visible uniquement pour les affiliés
 *   - `role: 'b2b_admin'` → visible uniquement pour managers B2B
 */
import {
  Home, Compass, Sparkles, Rocket, Eye, FileVideo,
  Bot, MessageSquare, Mic, Award, Shield as ShieldLock,
  Target, CheckCircle2, TrendingUp, FileBadge, Layers,
  Users, Calendar, Handshake,
  User, BookOpen, Receipt, Building2, CreditCard,
  RefreshCw, Gift,
  ArrowLeft, X as XIcon, Share2, Download, Play, Pause, VolumeX, LogOut,
} from 'lucide-react';
import { DEMO_SCENE_COUNT, DEMO_TOTAL_DURATION_MIN } from '../data/demoScenes.config';

/** ─────────── SECTION 1 — 🚀 DÉMARRER ─────────── */
const SECTION_DEMARRER = {
  id: 'demarrer',
  emoji: '🚀',
  title: 'Démarrer',
  subtitle: 'Comprenez CoachDIA en un coup d\'œil',
  items: [
    {
      id: 'logo',
      icon: Home,
      label: 'Logo CoachDIA',
      what: 'Votre point d\'ancrage dans la plateforme.',
      use: 'Revenir à l\'accueil ou au dashboard.',
      when: 'À tout moment pour repartir de zéro.',
    },
    {
      id: 'menu-comprendre',
      icon: Compass,
      label: 'Comprendre',
      what: 'Groupe de navigation Vision + Formation.',
      use: 'Découvrir la méthode et les 5 piliers.',
      when: 'Avant de vous lancer.',
      device: ['desktop'],
    },
    {
      id: 'menu-experimenter',
      icon: Sparkles,
      label: 'Expérimenter',
      what: 'Groupe Coach IA + Coaching humain.',
      use: 'Comparer les deux modes d\'accompagnement.',
      when: 'Pour choisir votre voie.',
      device: ['desktop'],
    },
    {
      id: 'menu-analyser',
      icon: Eye,
      label: 'Analyser',
      what: 'Groupe Prism + Pulse.',
      use: 'Lancer un diagnostic comportemental ou équipe.',
      when: 'Pour objectiver votre profil.',
      device: ['desktop'],
    },
    {
      id: 'demo',
      icon: FileVideo,
      label: 'Démo interactive',
      what: `Parcours de ${DEMO_SCENE_COUNT} scènes avec narration.`,
      use: `Comprendre CoachDIA en ${DEMO_TOTAL_DURATION_MIN} minutes.`,
      when: 'Premier contact avec la plateforme.',
    },
    {
      id: 'cta-demarrer',
      icon: Rocket,
      label: 'Commencer',
      what: 'CTA principal de la page d\'accueil.',
      use: 'Accéder aux plans et s\'inscrire.',
      when: 'Vous êtes prêt à vous lancer.',
    },
  ],
};

/** ─────────── SECTION 2 — 🤖 PROGRESSER AVEC OWEN ─────────── */
const SECTION_OWEN = {
  id: 'owen',
  emoji: '🤖',
  title: 'Progresser avec Owen',
  subtitle: 'Votre coach IA CoachDIA',
  // Bloc pédagogique spécial affiché en tête de section
  highlight: {
    title: 'Owen, votre coach IA CoachDIA',
    body: 'Une seule IA, trois modes d\'usage clairs :',
    bullets: [
      { icon: MessageSquare, label: 'Owen Libre', desc: 'Vous aide librement, hors parcours.' },
      { icon: Target, label: 'Owen Parcours', desc: 'Vous fait progresser dans les modules.' },
      { icon: Award, label: 'Owen Certifiant', desc: 'Vous évalue et valide vos compétences.' },
    ],
  },
  items: [
    {
      id: 'owen-libre',
      icon: MessageSquare,
      label: 'Owen Libre',
      what: 'Échange libre avec Owen, hors parcours certifiant.',
      use: 'Obtenir de l\'aide, clarifier une situation, vous entraîner.',
      when: 'Entre deux modules, pour une question ponctuelle.',
    },
    {
      id: 'owen-vocal',
      icon: Mic,
      label: 'Owen Vocal',
      what: 'Conversation audio temps réel avec Owen.',
      use: 'Coaching immersif à l\'oral.',
      when: 'Préparer un entretien, un feedback difficile.',
    },
    {
      id: 'owen-ecrit',
      icon: BookOpen,
      label: 'Owen Écrit',
      what: 'Chat texte structuré avec Owen.',
      use: 'Réfléchir posément, garder une trace écrite.',
      when: 'Quand vous voulez formaliser votre pensée.',
    },
    {
      id: 'owen-certifiant',
      icon: Award,
      label: 'Owen Certifiant',
      what: 'Owen en mode évaluation dans un module.',
      use: 'Valider une compétence avec preuves observables.',
      when: 'Uniquement pendant un parcours certifiant.',
    },
  ],
};

/** ─────────── SECTION 3 — 🏆 SUIVRE MON PARCOURS ─────────── */
const SECTION_PARCOURS = {
  id: 'parcours',
  emoji: '🏆',
  title: 'Suivre mon parcours',
  subtitle: 'Une progression structurée et certifiable',
  highlight: {
    title: 'Le parcours certifiant en 4 points',
    body: 'Un système pensé pour la preuve, pas l\'accumulation.',
    bullets: [
      { icon: Target, label: 'Des objectifs', desc: 'Clairs et mesurables.' },
      { icon: CheckCircle2, label: 'Des critères', desc: 'Explicites et partagés.' },
      { icon: TrendingUp, label: 'Des preuves', desc: 'Observables dans la durée.' },
      { icon: Award, label: 'Une validation', desc: 'Réelle, pas déclarative.' },
    ],
  },
  items: [
    {
      id: 'module',
      icon: Layers,
      label: 'Module',
      what: 'Unité pédagogique : théorie + exercice + évaluation.',
      use: 'Travailler une compétence précise.',
      when: 'Selon votre plan de progression.',
    },
    {
      id: 'pilier',
      icon: FileBadge,
      label: 'Pilier',
      what: 'Regroupement de 4 modules.',
      use: 'Valider un niveau de maîtrise.',
      when: 'Objectif intermédiaire vers la certification.',
    },
    {
      id: 'certificat',
      icon: Award,
      label: 'Certificat',
      what: 'Attestation générée après validation.',
      use: 'Preuve officielle de votre maîtrise.',
      when: 'À la fin d\'un pilier validé.',
    },
    {
      id: 'prism',
      icon: Eye,
      label: 'Prism',
      what: 'Profil comportemental individuel.',
      use: 'Comprendre vos tendances naturelles.',
      when: 'Avant de définir vos objectifs.',
    },
    {
      id: 'pulse',
      icon: TrendingUp,
      label: 'Pulse',
      what: 'Diagnostic de maturité collective.',
      use: 'Mesurer la santé managériale d\'une équipe.',
      when: 'Lancer ou suivre un diagnostic d\'équipe.',
    },
  ],
};

/** ─────────── SECTION 4 — 👥 ÊTRE ACCOMPAGNÉ PAR UN COACH HUMAIN ─────────── */
const SECTION_COACH_HUMAIN = {
  id: 'coach-humain',
  emoji: '👥',
  title: 'Être accompagné par un coach humain',
  subtitle: 'La continuité IA ↔ humain, sous votre contrôle',
  highlight: {
    title: 'Contexte partagé Owen → coach humain',
    body: 'Votre coach humain peut recevoir une synthèse de votre progression avec Owen, uniquement après votre validation explicite.',
    badge: '🔒 Vous gardez le contrôle total',
    rgpd: true,
  },
  items: [
    {
      id: 'espace-coach',
      icon: Handshake,
      label: 'Espace Coach',
      what: 'Marketplace des coachs certifiés CoachDIA.',
      use: 'Choisir et réserver un coach humain.',
      when: 'Après avoir progressé avec Owen.',
    },
    {
      id: 'mes-sessions',
      icon: Calendar,
      label: 'Mes sessions',
      what: 'Vos bookings à venir et passés.',
      use: 'Gérer vos créneaux, rejoindre la visio.',
      when: 'Après chaque réservation.',
    },
    {
      id: 'partage-contexte',
      icon: ShieldLock,
      label: 'Partager mon contexte',
      what: 'Bouton de consentement RGPD sur votre booking.',
      use: 'Autoriser (ou révoquer) le partage de contexte Owen.',
      when: 'Avant votre séance humaine, pour gagner du temps.',
    },
  ],
};

/** ─────────── SECTION 5 — ⚙️ GÉRER MON COMPTE ─────────── */
const SECTION_COMPTE = {
  id: 'compte',
  emoji: '⚙️',
  title: 'Gérer mon compte',
  subtitle: 'Profil, factures, abonnement',
  items: [
    {
      id: 'dashboard',
      icon: User,
      label: 'Mon Dashboard',
      what: 'Votre espace personnel CoachDIA.',
      use: 'Vue d\'ensemble de votre progression et vos accès.',
      when: 'Point de départ après connexion.',
    },
    {
      id: 'profil',
      icon: User,
      label: 'Mon compte / Profil',
      what: 'Informations personnelles.',
      use: 'Modifier email, nom, mot de passe.',
      when: 'Mise à jour de vos infos.',
    },
    {
      id: 'pedagogie',
      icon: BookOpen,
      label: 'Pédagogie',
      what: 'Suivi de votre progression (onglet).',
      use: 'Voir vos modules, scores, piliers validés.',
      when: 'Tous les jours pour piloter votre progression.',
    },
    {
      id: 'factures',
      icon: Receipt,
      label: 'Factures',
      what: 'Historique de paiement + téléchargement PDF.',
      use: 'Comptabilité, justificatifs, archive.',
      when: 'Fin de mois ou pour votre service compta.',
    },
    {
      id: 'mon-contexte',
      icon: ShieldLock,
      label: 'Mon contexte',
      what: 'Liste de vos partages Owen → coach (onglet RGPD).',
      use: 'Voir, modifier ou révoquer vos partages.',
      when: 'À tout moment pour contrôler vos données.',
    },
    {
      id: 'entreprise',
      icon: Building2,
      label: 'Entreprise',
      what: 'Dashboard entreprise (admin B2B).',
      use: 'Piloter les licences et les membres.',
      when: 'Si vous êtes admin de votre entreprise.',
      role: 'b2b_admin',
    },
    {
      id: 'abonnement',
      icon: CreditCard,
      label: 'Abonnement',
      what: 'Gestion du pack Découverte / Standard / Pro.',
      use: 'Upgrade ou downgrade instantané.',
      when: 'Selon l\'évolution de vos besoins.',
    },
    {
      id: 'recharge',
      icon: Gift,
      label: 'Recharge sessions',
      what: 'Pack à l\'unité, hors abonnement.',
      use: 'Acheter des sessions supplémentaires.',
      when: 'Quand votre quota mensuel est épuisé.',
    },
    {
      id: 'affiliation',
      icon: Users,
      label: 'Affiliation',
      what: 'Programme de parrainage avec commission.',
      use: 'Gagner des revenus récurrents.',
      when: 'Quand vous recommandez CoachDIA.',
      role: 'affiliate',
    },
  ],
};

/** ─────────── SECTION 6 — 🔄 ACTIONS RAPIDES ─────────── */
const SECTION_ACTIONS = {
  id: 'actions',
  emoji: '🔄',
  title: 'Actions rapides',
  subtitle: 'Les contrôles qui reviennent partout',
  items: [
    { id: 'retour',    icon: ArrowLeft, label: 'Retour (←)',  what: 'Flèche en haut à gauche.', use: 'Revenir à l\'écran précédent.',              when: 'Quand vous avez fini une sous-section.' },
    { id: 'fermer',    icon: XIcon,     label: 'Fermer (✕)', what: 'Croix en haut à droite.', use: 'Fermer une modale ou un panneau.',          when: 'Après lecture ou sélection.' },
    { id: 'partager',  icon: Share2,    label: 'Partager',    what: 'Boutons LinkedIn / X / Copier / natif mobile.', use: 'Envoyer un lien CoachDIA.', when: 'Réseau pro, message privé.' },
    { id: 'download',  icon: Download,  label: 'Télécharger', what: 'Bouton de téléchargement PDF / MP4.',       use: 'Sauvegarder en local.',                    when: 'Backup, archive personnelle.' },
    { id: 'play',      icon: Play,      label: 'Lecture',    what: 'Bouton Lecture (démo, audio).',             use: 'Reprendre la narration et la musique.',   when: 'Après une pause.' },
    { id: 'pause',     icon: Pause,     label: 'Pause',      what: 'Bouton Pause (démo, audio).',               use: 'Suspendre narration + musique.',           when: 'Interruption courte.' },
    { id: 'mute',      icon: VolumeX,   label: 'Mute',       what: 'Bouton son (démo, audio).',                 use: 'Couper tout le son.',                       when: 'Dans un lieu public.' },
    { id: 'logout',    icon: LogOut,    label: 'Déconnexion', what: 'Bouton en haut à droite.',                use: 'Fermer votre session.',                    when: 'Fin de session, appareil partagé.' },
  ],
};

export const NAVIGATION_HELP_SECTIONS = [
  SECTION_DEMARRER,
  SECTION_OWEN,
  SECTION_PARCOURS,
  SECTION_COACH_HUMAIN,
  SECTION_COMPTE,
  SECTION_ACTIONS,
];

/** Mini glossaire visuel (grid 3× mobile / 4× desktop). */
export const NAVIGATION_HELP_GLOSSARY = [
  { icon: Home,         label: 'Accueil',     hint: 'Retour home' },
  { icon: User,         label: 'Profil',      hint: 'Mon compte' },
  { icon: BookOpen,     label: 'Modules',     hint: 'Pédagogie' },
  { icon: Target,       label: 'Objectifs',   hint: 'Parcours' },
  { icon: Bot,          label: 'Owen',        hint: 'Coach IA' },
  { icon: Handshake,    label: 'Coach',       hint: 'Humain' },
  { icon: Award,        label: 'Certificat',  hint: 'Validation' },
  { icon: Receipt,      label: 'Factures',    hint: 'Comptabilité' },
  { icon: ShieldLock,   label: 'RGPD',        hint: 'Mes partages' },
  { icon: CreditCard,   label: 'Abonnement',  hint: 'Pack mensuel' },
  { icon: Gift,         label: 'Recharge',    hint: 'Sessions sup.' },
  { icon: FileVideo,    label: 'Démo',        hint: 'Présentation' },
  { icon: Share2,       label: 'Partager',    hint: 'Réseau / lien' },
  { icon: Download,     label: 'Télécharger', hint: 'PDF / MP4' },
  { icon: RefreshCw,    label: 'Actualiser',  hint: 'Recharger' },
  { icon: LogOut,       label: 'Déconnexion', hint: 'Sortir' },
];

/** Filtrage des items selon le rôle utilisateur. */
export function filterHelpItems(sections, { role, isAffiliate, isB2BAdmin } = {}) {
  return sections.map((s) => ({
    ...s,
    items: s.items.filter((it) => {
      if (!it.role) return true;
      if (it.role === 'affiliate') return isAffiliate || role === 'affiliate';
      if (it.role === 'b2b_admin') return isB2BAdmin;
      return true;
    }),
  }));
}
